/**
 * use-task-dashboard.ts
 * 
 * Hook per recuperare statistiche avanzate dei task per la dashboard.
 * Include completion rate, distribuzione per status, task overdue e workload utenti.
 */

import { useQuery } from '@tanstack/react-query';
import { useCacheConfig } from '../contexts/cache-config-context';
import { tasksNew, usersNew } from '../services/api';
import { isTaskCompleted } from '../utils/task-status-utils';

export interface TaskStats {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  overdue: number;
  completionRate: number;
}

export interface UserWorkload {
  userId: number;
  userName: string;
  userEmail: string;
  totalTasks: number;
  activeTasks: number;
  overdueTasks: number;
  completionRate: number;
}

export function useTaskDashboardStats(requestId?: string) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['task-dashboard-stats', requestId],
    queryFn: async (): Promise<TaskStats> => {
      // Recupera tutti i task (filtrati per requestId se fornito)
      const response = requestId 
        ? await tasksNew.getTasksByRequest(requestId)
        : await tasksNew.getAllTasks();

      // Gestisce sia array diretto che risposta paginata
      const tasks = Array.isArray(response) ? response : response.items || [];
      const now = new Date();
      
      // Nel nuovo modello Planner gli stati intermedi (Ideas/ToDo/Testing) sono stati
      // normalizzati a "Doing" ("Iniziato"): usiamo isTaskCompleted per il bucket attivo/
      // completato invece di confrontare stringhe di stato puntuali, che non esistono più.
      const stats = {
        total: tasks.length,
        todo: tasks.filter((t: any) => t.statusString === 'ToDo').length,
        inProgress: tasks.filter((t: any) => !isTaskCompleted(t)).length,
        done: tasks.filter((t: any) => isTaskCompleted(t)).length,
        overdue: tasks.filter((t: any) => {
          if (!t.dueDate || isTaskCompleted(t)) return false;
          return new Date(t.dueDate) < now;
        }).length,
        completionRate: tasks.length > 0
          ? (tasks.filter((t: any) => isTaskCompleted(t)).length / tasks.length) * 100
          : 0,
      };

      return stats;
    },
    enabled: true,
    staleTime: config.tasks,
    gcTime: config.tasks * 2,
  });
}

export function useUserWorkloadStats(requestId?: string) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['user-workload-stats', requestId],
    queryFn: async (): Promise<UserWorkload[]> => {
      // Recupera tutti gli utenti attivi e i task in parallelo
      const [usersResponse, tasksResponse] = await Promise.all([
        usersNew.getActiveUsers(),
        requestId 
          ? tasksNew.getTasksByRequest(requestId)
          : tasksNew.getAllTasks()
      ]);

      // Gestisce sia array diretto che risposta paginata
      const tasks = Array.isArray(tasksResponse) ? tasksResponse : tasksResponse.items || [];
      const users = usersResponse || [];
      const now = new Date();
      
      // Crea una mappa per lookup veloce degli utenti
      const userMap = new Map(users.map(user => [parseInt(user.id), user]));
      
      // Raggruppa task per utente
      const userTaskMap = new Map<number, {
        userId: number;
        userName: string;
        userEmail: string;
        tasks: any[];
      }>();

      // Preferisce task.assignees (multi-assegnatario) quando presente, incrementando il
      // carico di lavoro per ogni assegnatario del task; ricade sul vecchio campo singolo
      // assignedToUserId solo quando assignees è assente (endpoint legacy).
      tasks.forEach((task: any) => {
        const assignees: { userId: number; fullName?: string }[] =
          Array.isArray(task.assignees) && task.assignees.length > 0
            ? task.assignees
            : task.assignedToUserId
              ? [{ userId: task.assignedToUserId, fullName: task.assignedToUser?.fullName }]
              : [];

        assignees.forEach((assignee) => {
          const existing = userTaskMap.get(assignee.userId);
          const user = userMap.get(assignee.userId);

          const userData = {
            userId: assignee.userId,
            userName: assignee.fullName ||
                     user?.fullName ||
                     (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '') ||
                     `Utente ${assignee.userId}`,
            userEmail: user?.email || '',
            tasks: existing ? [...existing.tasks, task] : [task],
          };

          userTaskMap.set(assignee.userId, userData);
        });
      });

      // Calcola statistiche per utente
      const userWorkloads: UserWorkload[] = Array.from(userTaskMap.values()).map(userData => {
        const userTasks = userData.tasks;
        const activeTasks = userTasks.filter(t => !isTaskCompleted(t)).length;
        const overdueTasks = userTasks.filter(t => {
          if (!t.dueDate || isTaskCompleted(t)) return false;
          return new Date(t.dueDate) < now;
        }).length;
        const completedTasks = userTasks.filter(t => isTaskCompleted(t)).length;

        return {
          userId: userData.userId,
          userName: userData.userName,
          userEmail: userData.userEmail,
          totalTasks: userTasks.length,
          activeTasks,
          overdueTasks,
          completionRate: userTasks.length > 0 ? (completedTasks / userTasks.length) * 100 : 0,
        };
      });

      // Ordina per numero di task attivi (workload)
      return userWorkloads.sort((a, b) => b.activeTasks - a.activeTasks);
    },
    enabled: true,
    staleTime: config.tasks,
    gcTime: config.tasks * 2,
  });
}

export function useTaskDashboard(requestId?: string) {
  const statsQuery = useTaskDashboardStats(requestId);
  const workloadQuery = useUserWorkloadStats(requestId);

  return {
    stats: statsQuery.data,
    userWorkloads: workloadQuery.data || [],
    isLoading: statsQuery.isLoading || workloadQuery.isLoading,
    isError: statsQuery.isError || workloadQuery.isError,
    error: statsQuery.error || workloadQuery.error,
    refetch: () => {
      statsQuery.refetch();
      workloadQuery.refetch();
    },
  };
}